import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { DateTime as Luxon } from "luxon";

import PersonPreview from "components/PersonPreview";
import Form from "components/Form/Form";
import { sendReport } from "domain/Game/api";
import Modal from "components/Modal";
import { noop, reduce } from "lodash-es";
import { gameTypes } from "domain/GamesSearch/types";
import { cityTypes } from "domain/Directories/types";
import { toast } from "react-toastify";
import classNames from "classnames";
import PlayerReportItemButtons from "components/PlayersReport/Buttons";
import PlayerReportTags from "components/PlayersReport/Tags";
import PaidSelect from "../../../shared/PaidSelect/PaidSelect";

const PlayersReport = ({
    mainCast,
    gameId,
    isOrganizer,
    needReport,
    startsAt,
    city,
    updateGameData,
    updateBlackList,
    onPlayerClick = noop,
    userGroups,
    updateGroupData,
    isMobile,
    blacklists,
    ...props
}) => {
    const [showSuccess, setShowSuccess] = useState(false);
    const [game, setGame] = useState(props.game || {});
    const [missedPlayers, setMissedPlayers] = useState({});
    const [unpaidPlayers, setUnpaidPlayers] = useState([]);
    const toggleShowSuccess = () => setShowSuccess((show) => !show);

    const DateTime = Luxon.fromSQL(startsAt);
    const nextDate = DateTime.plus({ days: 7 });

    const onSuccess = (response) => {
        updateGameData(gameId, response.game);
        setGame(response.game);
        toggleShowSuccess();
    };

    const onSubmit = async () => {
        try {
            const response = await sendReport(
                gameId,
                reduce(
                    missedPlayers,
                    (accum, checked, id) => (checked ? accum : [...accum, id]),
                    []
                ),
                unpaidPlayers
            );

            onSuccess(response);
        } catch (e) {
            toast.error(
                "Не удалось отправить отчет, попробуйте перезагрузить страницу и поправить снова"
            );
        }
    };

    const changeMissedPlayers = (event) => {
        const id = event.target.name;
        setMissedPlayers((prevState) => ({
            ...prevState,
            [id]: event.target.checked,
        }));
    };

    const changeUnpaidPlayers = (id, isPaid) => {
        if (isPaid === "false" && !unpaidPlayers.includes(id)) {
            setUnpaidPlayers([...unpaidPlayers, id]);
        }
    };

    return (
        <>
            <h3 className="fs-h3 c-gs-hight-em mb-16-24">Игроки</h3>

            <div
                className="card-full__report report"
                style={mainCast.length > 0 ? {} : { display: "none" }}
            >
                {isOrganizer && needReport && (
                    <p className="c-gs-label mb-40">
                        Уберите галочку у тех имен, кто не пришел на игру.
                    </p>
                )}
                <Form
                    id="makeGameReport"
                    submit={needReport ? onSubmit : noop}
                    onSuccess={noop}
                    onError={noop}
                >
                    {() => (
                        <>
                            <input type="hidden" name="id" value={gameId} />
                            {mainCast.map((player) => {
                                const {
                                    id,
                                    name,
                                    avatar,
                                    family_name,
                                    is_came,
                                    is_online_pay,
                                    is_paid,
                                } = player;

                                return (
                                    <div
                                        className={classNames(`report__item`, {
                                            "report__item--disabled": !is_came,
                                        })}
                                        key={`player-report-item-${id}`}
                                    >
                                        <div className="report__item-main">
                                            <div className="report__item-user">
                                                <PersonPreview
                                                    name={name}
                                                    family_name={family_name}
                                                    avatar={avatar}
                                                    mods={
                                                        "person--row flex-grow-1"
                                                    }
                                                    onClick={onPlayerClick}
                                                    id={id}
                                                    isMobile={isMobile}
                                                    paidSelectProp={{
                                                        is_online_pay,
                                                        is_paid,
                                                        changeUnpaidPlayers,
                                                        needReport,
                                                        is_came,
                                                    }}
                                                    isOrganizer={isOrganizer}
                                                />
                                            </div>

                                            {isOrganizer &&
                                                !isMobile &&
                                                is_came && (
                                                    <PaidSelect
                                                        id={id}
                                                        is_online_pay={
                                                            is_online_pay
                                                        }
                                                        is_paid={is_paid}
                                                        changeUnpaidPlayers={
                                                            changeUnpaidPlayers
                                                        }
                                                        needReport={needReport}
                                                    />
                                                )}

                                            <PlayerReportItemButtons
                                                id={id}
                                                is_came={is_came}
                                                changeMissedPlayers={
                                                    changeMissedPlayers
                                                }
                                                needReport={needReport}
                                                isOrganizer={isOrganizer}
                                            />
                                        </div>

                                        <PlayerReportTags
                                            id={id}
                                            player={player}
                                            userGroups={userGroups}
                                            blacklists={blacklists}
                                            isOrganizer={isOrganizer}
                                            updateGroupData={updateGroupData}
                                            updateBlackList={updateBlackList}
                                            isMobile={isMobile}
                                        />
                                    </div>
                                );
                            })}
                        </>
                    )}
                </Form>

                {showSuccess && (
                    <Modal
                        onClose={toggleShowSuccess}
                        title="Отчет отправлен!"
                        visible
                        closeOnOverlay
                        modificators={[`small-col`]}
                    >
                        <p className="modal__info text-center">
                            Создать новую игру на{" "}
                            <b className="flex text-nowrap">
                                {nextDate
                                    .setLocale(`ru`)
                                    .toFormat("EEEE, d MMMM yyyy")}
                                ?
                            </b>
                        </p>

                        <div className="mt-32">
                            <Link
                                to={
                                    `/${city.code}/game/create?game=` +
                                    JSON.stringify({
                                        id: game.id,
                                        starts_at: nextDate.toSQL({
                                            includeZone: false,
                                            includeOffset: false,
                                        }),
                                        start_time: nextDate.toFormat(
                                            Luxon.TIME_24_SIMPLE
                                        ),
                                        start_date: nextDate.toFormat(
                                            "dd.LL.yyyy"
                                        ),
                                    })
                                }
                                className="btn btn--medium btn--primary"
                            >
                                Создать
                            </Link>
                        </div>
                    </Modal>
                )}
            </div>
        </>
    );
};

PlayersReport.propTypes = {
    mainCast: PropTypes.arrayOf(PropTypes.object),
    gameId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isOrganizer: PropTypes.bool,
    startsAt: PropTypes.string,
    needReport: PropTypes,
    game: gameTypes,
    city: cityTypes,
    updateGameData: PropTypes.func.isRequired,
    updateGroupData: PropTypes.func.isRequired,
    updateBlackList: PropTypes.func.isRequired,
    onPlayerClick: PropTypes.func,
    userGroups: PropTypes.array,
    blacklists: PropTypes.array,
    selectedUser: PropTypes.number,
    isMobile: PropTypes.bool,
};

export default PlayersReport;
